/**
 * Persistent, dismissible banner shown when new price data is detected.
 *
 * Rendered between the top navigation header and the page content in
 * Layout.jsx.  Visible across all pages (not just the comparison table)
 * so the user always knows when fresh data is available.
 *
 * Actions:
 *   - Admin users: "click to refresh" triggers the refresh-data API
 *     (which syncs from Drive + ingests), then reloads the page.
 *   - Viewer users: "click to refresh" reloads the page to pick up
 *     any data already ingested by an admin.
 *   - dismiss (x) — hides the banner and advances the freshness baseline
 *     so the same data won't re-trigger the notification.
 *
 * Spec reference: section 6.14 (Frontend: New Data Banner).
 */
import { useCallback, useState } from 'react';
import { useDataFreshness } from '../hooks/useDataFreshness';
import { useAuth } from '../hooks/useAuth';
import { refreshData } from '../utils/api';

export default function NewDataBanner() {
  const { newDataAvailable, dismiss } = useDataFreshness();
  const { isAdmin } = useAuth();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    if (!isAdmin) {
      // Viewers can only reload to pick up already-ingested data
      window.location.reload();
      return;
    }

    setRefreshing(true);
    try {
      await refreshData();
      dismiss();
      window.location.reload();
    } catch {
      // Fall back to simple page reload on error
      window.location.reload();
    }
  }, [isAdmin, dismiss]);

  if (!newDataAvailable) return null;

  return (
    <div className="bg-blue-50 border-b border-blue-200" role="status">
      <div className="mx-auto px-4 sm:px-6 py-2">
        <div className="flex items-center justify-between gap-3">
          {/* Message */}
          <div className="flex items-center gap-2 min-w-0">
            {/* Info-circle icon */}
            <svg
              className="h-4 w-4 text-blue-500 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708
                   2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9
                   0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
              />
            </svg>
            <p className="text-sm text-blue-700">
              <span className="font-medium">New price data available</span>
              {' \u2014 '}
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className="font-medium underline hover:no-underline
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {refreshing ? 'refreshing\u2026' : 'click to refresh'}
              </button>
            </p>
          </div>

          {/* Dismiss button */}
          <button
            type="button"
            onClick={dismiss}
            className="flex-shrink-0 text-blue-400 hover:text-blue-600
                       transition-colors"
            aria-label="Dismiss new data notification"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
